import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

import i18n from '../../../i18n';
import { clampColumnWidth } from '../../../utils/gridColumnLayout';
import { GRID_ACTIONS_CELL_ATTR } from '../../../utils/gridColumns';
import {
    findHorizontalScroller,
    startDragAutoScroll
} from '../../../utils/gridDragAutoScroll';
import {
    GUIDE_WIDTH,
    resizeGuideSx,
    resizeHandleSx
} from './GridColumnResizeHandle.styles';

const KEYBOARD_STEP = 16;

// The body takes the resize cursor for the whole drag (the pointer leaves the
// thin handle at once) and text selection is off, so the drag never paints a
// selection across the table.
const lockBody = () => {
    const { style } = document.body;
    const previous = { cursor: style.cursor, userSelect: style.userSelect };
    style.cursor = 'col-resize';
    style.userSelect = 'none';
    return () => {
        style.cursor = previous.cursor;
        style.userSelect = previous.userSelect;
    };
};

/**
 * Drag zone on the right edge of a head cell that resizes its column.
 *
 * Presentation only: the grid owns the widths. `onResizeStart` returns the
 * column's current width, `onResize` gets live widths (at most once a frame)
 * and `onResizeEnd` the final one, all already clamped to the column limits;
 * Escape ends the drag with `onResizeCancel` instead, the width back where
 * it started. The handle swallows the pointer and click so the head cell's
 * sort label never fires, and refuses to start a native drag (column
 * reordering).
 *
 * The edge follows the pointer, but stops at the pinned actions column once
 * columns run under it: the scroll area pans on near that edge and the
 * column keeps growing, so its edge never goes out of sight. A line runs
 * down the table from the edge while it is dragged. A press that does not
 * move changes nothing; a double-click (or Enter) fits the column to its
 * content through `onAutoFit`.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.column - Head entry (`id`, optional `minWidth`/`maxWidth`).
 * @param {Function} props.onResizeStart - `(id, headCell) => width` on drag start.
 * @param {Function} props.onResize - `(id, width)` while dragging.
 * @param {Function} props.onResizeEnd - `(id, width)` once released.
 * @param {Function} [props.onResizeCancel] - `(id)` once the drag is called
 *   off, or ends where it began.
 * @param {Function} [props.onAutoFit] - `(id, headCell)` on double-click.
 * @returns {JSX.Element} the handle
 */
export default function GridColumnResizeHandle({
    column,
    onResizeStart,
    onResize,
    onResizeEnd,
    onResizeCancel,
    onAutoFit
}) {
    const [active, setActive] = useState(false);
    const cleanupRef = useRef(null);
    const guideRef = useRef(null);
    const placeGuideRef = useRef(null);

    useEffect(() => () => cleanupRef.current?.(), []);
    // The guide mounts with the drag: place it before it is painted.
    useLayoutEffect(() => {
        if (active) placeGuideRef.current?.();
    }, [active]);

    const handlePointerDown = (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();

        const handle = event.currentTarget;
        const cell = handle.closest('th');
        const startX = event.clientX;
        const startWidth = onResizeStart(column.id, cell);
        const scroller = findHorizontalScroller(cell);
        const startScrollLeft = scroller?.scrollLeft ?? 0;
        const pinned = cell.parentElement.querySelector(
            `[${GRID_ACTIONS_CELL_ATTR}]`
        );
        // Where the pointer holds the edge, so the edge itself stops at the
        // pinned column rather than the pointer.
        const grabOffset = cell.getBoundingClientRect().right - startX;
        const getRightEdge = () => pinned?.getBoundingClientRect().left;
        const overflows = () => scroller?.scrollWidth > scroller?.clientWidth;
        const table = cell.closest('table');
        let clientX = startX;
        let width = startWidth;
        let moved = false;
        let frame = 0;

        // The guide runs down the visible part of the table at the edge.
        const placeGuide = () => {
            const guide = guideRef.current;
            if (!guide) return;
            const area = (scroller || table).getBoundingClientRect();
            const tableRect = table.getBoundingClientRect();
            const top = Math.max(tableRect.top, area.top, 0);
            const bottom = Math.min(
                tableRect.bottom,
                area.bottom,
                window.innerHeight
            );
            const x = cell.getBoundingClientRect().right;
            guide.style.top = `${top}px`;
            guide.style.height = `${Math.max(bottom - top, 0)}px`;
            guide.style.transform = `translateX(${x - GUIDE_WIDTH / 2}px)`;
            guide.style.opacity = x < area.left || x > area.right ? '0' : '';
        };
        placeGuideRef.current = placeGuide;
        const applyWidth = () => {
            onResize(column.id, width);
            placeGuide();
        };

        // Held at the pinned column, the edge keeps going: the column grows
        // by each auto-scroll step before the step, so there is room to pan.
        const autoScroll =
            scroller &&
            startDragAutoScroll(scroller, getRightEdge, {
                leftEdge: false,
                beforeScroll: (delta) => {
                    if (!pinned || !overflows()) return;
                    width = clampColumnWidth(width + delta, column);
                    applyWidth();
                }
            });

        handle.setPointerCapture(event.pointerId);
        const unlockBody = lockBody();
        setActive(true);

        // Width from the pointer and the distance the scroll area panned.
        const update = () => {
            let x = clientX;
            if (pinned && overflows()) {
                x = Math.min(x, getRightEdge() - grabOffset);
            }
            const panned = scroller ? scroller.scrollLeft - startScrollLeft : 0;
            width = clampColumnWidth(startWidth + x - startX + panned, column);
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(applyWidth);
        };

        const handleMove = (moveEvent) => {
            clientX = moveEvent.clientX;
            moved = moved || Math.abs(clientX - startX) > 2;
            autoScroll?.update(clientX);
            update();
        };

        let finish;
        const handleKey = (keyEvent) => {
            if (keyEvent.key !== 'Escape') return;
            keyEvent.preventDefault();
            keyEvent.stopPropagation();
            finish(true);
        };
        const handleEnd = () => finish(false);

        finish = (cancelled) => {
            cancelAnimationFrame(frame);
            autoScroll?.stop();
            scroller?.removeEventListener('scroll', update);
            window.removeEventListener('keydown', handleKey, true);
            handle.removeEventListener('pointermove', handleMove);
            handle.removeEventListener('pointerup', handleEnd);
            handle.removeEventListener('pointercancel', handleEnd);
            unlockBody();
            cleanupRef.current = null;
            placeGuideRef.current = null;
            setActive(false);
            if (cancelled || !moved) {
                onResize(column.id, startWidth);
                onResizeCancel?.(column.id);
            } else {
                onResizeEnd(column.id, width);
            }
        };

        handle.addEventListener('pointermove', handleMove);
        handle.addEventListener('pointerup', handleEnd);
        handle.addEventListener('pointercancel', handleEnd);
        scroller?.addEventListener('scroll', update);
        window.addEventListener('keydown', handleKey, true);
        cleanupRef.current = handleEnd;
    };

    const handleKeyDown = (event) => {
        // With a modifier the keys mean something else (Alt+Shift+arrows
        // move the column): leave them to the head cell.
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'Enter' && onAutoFit) {
            event.preventDefault();
            event.stopPropagation();
            onAutoFit(column.id, event.currentTarget.closest('th'));
            return;
        }
        const step = { ArrowLeft: -KEYBOARD_STEP, ArrowRight: KEYBOARD_STEP }[
            event.key
        ];
        if (!step) return;
        event.preventDefault();
        event.stopPropagation();
        const cell = event.currentTarget.closest('th');
        const width = clampColumnWidth(
            onResizeStart(column.id, cell) + step,
            column
        );
        onResize(column.id, width);
        onResizeEnd(column.id, width);
    };

    const stop = (event) => event.stopPropagation();

    return (
        <>
            <Box
                role="separator"
                aria-orientation="vertical"
                aria-label={i18n.t('common.resizeColumn')}
                tabIndex={0}
                data-active={active || undefined}
                draggable={false}
                onPointerDown={handlePointerDown}
                onKeyDown={handleKeyDown}
                onClick={stop}
                onDoubleClick={(event) => {
                    event.stopPropagation();
                    onAutoFit?.(column.id, event.currentTarget.closest('th'));
                }}
                onDragStart={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
                sx={resizeHandleSx}
            />
            {active &&
                createPortal(
                    <Box ref={guideRef} sx={resizeGuideSx} aria-hidden />,
                    document.body
                )}
        </>
    );
}

GridColumnResizeHandle.propTypes = {
    column: PropTypes.shape({
        id: PropTypes.string.isRequired,
        minWidth: PropTypes.number,
        maxWidth: PropTypes.number
    }).isRequired,
    onResizeStart: PropTypes.func.isRequired,
    onResize: PropTypes.func.isRequired,
    onResizeEnd: PropTypes.func.isRequired,
    onResizeCancel: PropTypes.func,
    onAutoFit: PropTypes.func
};
